import { useChatStore, useUserStore } from '@talon/web/src/context';
import { Avatar, Box, ButtonExtendedProps, Menu, ResponsiveContext, Text, TextProps, ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { Home, Logout, Organization, User, UserAdmin, UserExpert } from 'grommet-icons';
import { capitalize } from 'lodash';
import { observer } from 'mobx-react-lite';
import { usePostHog } from 'posthog-js/react';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { fetchApiRelease, formatBuildLabel, WEB_RELEASE } from '../../utils/release';
import { isTalentManagerUser } from '../../utils/userRoles';

export type UserMenuProps = {
  textColor?: TextProps['color'];
};

export const UserMenu = observer(({ textColor = 'accent-1' }: UserMenuProps) => {
  const userStore = useUserStore();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useContext(ThemeContext);
  const posthog = usePostHog();
  // In the header bar only the avatar shows; the name and agency head the dropdown instead
  const inBar = useContext(ResponsiveContext) !== 'small';
  const [apiRelease, setApiRelease] = useState<string | null>(null);
  const chatStore = useChatStore();
  const isSupportStaff = !!(userStore.user?.is_admin || userStore.user?.is_support_agent);

  useEffect(() => {
    fetchApiRelease().then(setApiRelease);
  }, []);

  // Support staff see how much of the support inbox they haven't read, kept current every minute
  useEffect(() => {
    if (!isSupportStaff) return;
    chatStore.fetchInboxUnread();
    const interval = setInterval(chatStore.fetchInboxUnread, 60_000);
    return () => clearInterval(interval);
  }, [isSupportStaff]);

  const doLogout = () => {
    userStore.logout();
    posthog.reset();
    navigate('/login');
  };

  const profile: ButtonExtendedProps = {
    label: (
      <Box alignSelf="center">
        <Text>Profile</Text>
      </Box>
    ),
    onClick: () => navigate('/profile'),
    icon: (
      <Box pad="small">
        <User />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/profile',
  };
  const account: ButtonExtendedProps = {
    label: <Box alignSelf="center">Account</Box>,
    onClick: () => navigate('/account'),
    icon: (
      <Box pad="small">
        <Organization />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/account',
  };
  const logout: ButtonExtendedProps = {
    label: <Box alignSelf="center">Logout</Box>,
    onClick: () => doLogout(),
    icon: (
      <Box pad="small">
        <Logout />
      </Box>
    ),
    justify: 'start',
  };

  const adminMenu: ButtonExtendedProps = {
    label: <Box alignSelf="center">Admin</Box>,
    onClick: () => navigate('/admin'),
    icon: (
      <Box pad="small">
        <UserAdmin />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/admin',
  };

  // The admin area has its own header without the regular app's navigation; this is the way back
  const appMenu: ButtonExtendedProps = {
    label: <Box alignSelf="center">Talon app</Box>,
    onClick: () => navigate('/'),
    icon: (
      <Box pad="small">
        <Home />
      </Box>
    ),
    justify: 'start',
  };

  // const payrollMenu: ButtonExtendedProps = {
  //   label: <Box alignSelf="center">Payroll</Box>,
  //   onClick: () => navigate('/payroll'),
  //   icon: (
  //     <Box pad="small">
  //       <Money />
  //     </Box>
  //   ),
  //   color: 'accent-1',
  //   justify: 'start',
  //   active: location.pathname === '/payroll',
  // };

  const tmProfile: ButtonExtendedProps = {
    label: (
      <Box alignSelf="center">
        <Text>Profile</Text>
      </Box>
    ),
    onClick: () => navigate('/talent-manager?tab=profile'),
    icon: (
      <Box pad="small">
        <UserExpert />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/talent-manager' && new URLSearchParams(location.search).get('tab') === 'profile',
  };

  const tmAccountSettings: ButtonExtendedProps = {
    label: <Box alignSelf="center">Account settings</Box>,
    onClick: () => navigate('/profile'),
    icon: (
      <Box pad="small">
        <User />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/profile',
  };

  const supportInboxMenu: ButtonExtendedProps = {
    label: (
      <Box direction="row" align="center" gap="xsmall" alignSelf="center">
        <Text>Support inbox</Text>
        {chatStore.inboxUnread > 0 && (
          <Box background="brand" round="999px" pad={{ horizontal: '7px' }}>
            <Text size="12px" weight={600} color="white">
              {chatStore.inboxUnread}
            </Text>
          </Box>
        )}
      </Box>
    ),
    onClick: () => navigate('/support-inbox'),
    icon: (
      <Box pad="small">
        <UserExpert />
      </Box>
    ),
    justify: 'start',
    active: location.pathname === '/support-inbox',
  };

  // Which web + API builds this user is running, so a bug report can say exactly what was live
  const buildInfo: ButtonExtendedProps = {
    label: (
      <Box alignSelf="center">
        <Text size="xsmall" color="text-meta">
          {formatBuildLabel(WEB_RELEASE, apiRelease)}
        </Text>
      </Box>
    ),
    onClick: () => {
      navigator.clipboard
        ?.writeText(`web ${WEB_RELEASE} api ${apiRelease ?? 'unknown'}`)
        .then(() => toast.info('Build info copied'));
    },
    justify: 'start',
  };

  const menuItems: ButtonExtendedProps[] = userStore.user?.is_admin
    ? [adminMenu, appMenu, profile, account, logout] // admin
    : userStore.user.performer_id
      ? [profile, logout] // performer user
      : isTalentManagerUser(userStore.user)
        ? [tmProfile, tmAccountSettings, logout] // talent manager
        : [profile, account, logout]; // agency user

  // Support Inbox is its own route (not a tab under /admin) so both admins and non-admin support
  // agents need a direct link to it, regardless of which role bucket they otherwise fall into above.
  if (isSupportStaff) {
    menuItems.unshift(supportInboxMenu);
  }
  menuItems.push(buildInfo);
  const fullName = `${userStore.user?.first_name} ${userStore.user?.last_name}`;
  const groupName = userStore.user?.active_group ? capitalize(userStore.user?.active_group?.name) : '';
  if (inBar) {
    // Who's signed in: a heading, not something to click
    menuItems.unshift({
      label: (
        <Box pad={{ vertical: 'xsmall' }}>
          <Text weight={600} color="text-heading">
            {fullName}
          </Text>
          {groupName && (
            <Text size="small" color="text-meta">
              {groupName}
            </Text>
          )}
        </Box>
      ),
      disabled: true,
      style: { opacity: 1, cursor: 'default' },
    });
  }
  // Light, with the page the user is on tinted - as in the header's project menus
  const items = menuItems.map(({ active, style, ...item }) => ({
    ...item,
    'aria-current': active ? ('page' as const) : undefined,
    style: { borderRadius: '8px', background: active ? normalizeColor('brand-tint', theme) : undefined, ...style },
  }));

  return (
    <Box>
      <Menu
        justifyContent="center"
        a11yTitle={`Account menu for ${fullName}`}
        dropAlign={{ top: 'bottom', right: 'right' }}
        dropProps={{ background: 'white', pad: 'xsmall', round: '8px', elevation: 'medium' }}
        icon={
          <Box direction="row" align="center" justify="center" wrap={true} gap="xsmall" pad={{ vertical: '3px' }}>
            <Box style={{ position: 'relative' }}>
              <Avatar background="accent-2" size="36px">
                {userStore.user?.first_name?.substring(0, 1)}
                {userStore.user?.last_name?.substring(0, 1)}
              </Avatar>
              {/* Unread support messages, for support staff, while the menu is closed */}
              {isSupportStaff && chatStore.inboxUnread > 0 && (
                <Box
                  a11yTitle={`${chatStore.inboxUnread} unread in the support inbox`}
                  width="10px"
                  height="10px"
                  round="full"
                  background="text-danger"
                  border={{ color: 'white', size: '2px' }}
                  style={{ position: 'absolute', top: 0, right: 0 }}
                />
              )}
            </Box>
            {!inBar && (
              <Box>
                <Text color={textColor}>{fullName}</Text>
                <Text size="small" weight={300} color={textColor}>
                  {groupName}
                </Text>
              </Box>
            )}
          </Box>
        }
        items={items}
      ></Menu>
    </Box>
  );
});
